import { Button, Flex, Grid, useBreakpointValue, useToast } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorToast } from "../../../../../../utils";
import { ConsumptionsGridHeaderRow, ConsumptionsGridItems } from "./index";
import { defaultScopeOneGridColumns, defaultScopeTwoGridColumns, SCOPES } from "../../helpers";
import { Paginator } from "../../../../../../components/Paginator";

// Static columns are 'Category', 'Value' and 'Measure'
export const NUMBER_OF_STATIC_COLS = 3;

// Action column contains the action buttons
export const NUMBER_OF_ACTION_COLS = 1;

function ConsumptionsGrid({
                            questionnaireId,
                            consumptionTypes,
                            scope,
                            sectionType,
                            consumptionTypesAttr,
                            handleAddItem,
                            handleEditItem,
                            handleDeleteItem,
                            getItemsFunction,
                            isPreview,
                            countries,
                            onDataItemsChange
                          }) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const addNewItemBtnLabel = useBreakpointValue({
    base: `+ ${t("buttons.add")}`,
    md: `+ ${t("buttons.addNewItems")}`
  });

  const [itemsList, setItemsList] = useState([]);
  const [paginatorObj, setPaginatorObj] = useState({
    queryPageIndex: 0,
    totalPages: 0
  });
  const queryPageSize = 10;

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      const customItem = {
        id: item.id ?? null
      };

      if (scope === SCOPES.SCOPE_ONE) {
        customItem["consumptionType"] = item.consumptionTypeId ?? "";
      } else {
        customItem["countryId"] = item.countryId ?? "";
        customItem["electricityType"] = item.electricityType ?? "";
      }
      customItem["consumptionValue"] = item.consumption?.value ?? "";
      customItem["consumptionMeasure"] = item.consumption?.measureId ?? "";

      return customItem;
    });
  }, [scope]);

  getItemsFunction({
      queryParams: {
        page: paginatorObj.queryPageIndex, size: queryPageSize, sort: "id,desc"
      },
      type: sectionType,
      questionnaireId: questionnaireId
    },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const items = prepareSectionValues(pageContent);
        setItemsList(items);
        onDataItemsChange(consumptionTypesAttr, items.length);
        if (pageContent.length === 0 && page?.number !== 0 && page?.totalElements !== 0 && paginatorObj.queryPageIndex > 0) {
          setPaginatorObj({
            queryPageIndex: paginatorObj.queryPageIndex - 1,
            totalPages: page?.totalPages ?? 0
          });
        } else {
          setPaginatorObj({
            queryPageIndex: page?.number ?? 0,
            totalPages: page?.totalPages ?? 0
          });
        }
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const handlePageSelection = (newPage) => {
    setPaginatorObj({
      ...paginatorObj,
      queryPageIndex: newPage - 1
    });
  };

  return (
    <Fragment>
      <Flex mb={5} gap={"25%"} overflow="auto" justifyContent={isPreview ? "center" : "start"}>
        {!isPreview ?
          <Button
            colorScheme="white"
            color="esg_lightgreen"
            border={"1px solid"}
            borderColor={"esg_lightgreen"}
            _focus={{ boxShadow: "none" }}
            minWidth={16}
            onClick={handleAddItem}>
            {addNewItemBtnLabel}
          </Button> : null
        }
        {itemsList.length > 0 &&
          <Paginator
            selectedPage={paginatorObj.queryPageIndex + 1}
            numberOfPages={paginatorObj.totalPages}
            handlePageSelection={(newPage) => handlePageSelection(newPage)}
          />}
      </Flex>
      {
        /* Only the action column (if available) has colSpan = 1, and all other columns have colSpan = 2 */
        /* The columns with colSpan = 2 are Category, Value and Measure (and Country if we are in scope two) */
      }
      {itemsList.length > 0 &&
        <Grid
          templateColumns={`repeat(${(NUMBER_OF_STATIC_COLS + (scope === SCOPES.SCOPE_TWO ? 1 : 0)) * 2 + (!isPreview ? NUMBER_OF_ACTION_COLS : 0)}, 1fr)`}
          minWidth="700"
          maxHeight="400"
          overflow="auto"
        >
          <ConsumptionsGridHeaderRow
            isPreview={isPreview}
            columnNames={!isPreview ?
              scope === SCOPES.SCOPE_ONE ? [...defaultScopeOneGridColumns, ""] : [...defaultScopeTwoGridColumns, ""] :
              scope === SCOPES.SCOPE_ONE ? defaultScopeOneGridColumns : defaultScopeTwoGridColumns
            }
          />
          <ConsumptionsGridItems
            consumptionTypes={consumptionTypes}
            items={itemsList}
            handleEditItem={handleEditItem}
            handleDeleteItem={handleDeleteItem}
            scope={scope}
            consumptionTypesAttr={consumptionTypesAttr}
            countries={countries}
            isPreview={isPreview}
          />
        </Grid>}
    </Fragment>
  );
}

export default ConsumptionsGrid;